import { canRemoveMedia, type BoardItem, type Viewer } from "@boem/core/shared";
import { useEffect, useState } from "react";

export function Lightbox({
  item,
  start,
  onClose,
  viewer,
  onRemove,
}: {
  item: BoardItem;
  start: number;
  onClose: () => void;
  viewer?: Viewer;
  onRemove?: (id: string) => void;
}) {
  const [i, setI] = useState(start);
  const shots = item.shots;
  const n = shots.length;
  const step = (d: number) => setI((cur) => (cur + d + n) % n);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") return onClose();
      // arrow keys on a focused video seek it instead
      if ((e.target as HTMLElement | null)?.tagName === "VIDEO") return;
      if (e.key === "ArrowLeft") step(-1);
      else if (e.key === "ArrowRight") step(1);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });
  useEffect(() => {
    if (i >= n && n > 0) setI(n - 1);
  }, [i, n]);
  const s = shots[Math.min(i, n - 1)];
  if (!s) return null;
  const video = s.kind === "video";
  const thumbOnly = !video && !s.full;
  const noun = video ? "recording" : "screenshot";
  const state =
    s.scan === "quarantined"
      ? ` · hidden from the team: looks like a credential${s.findings.length ? ` (${s.findings.join(", ")})` : ""}`
      : s.scan === "pending"
        ? s.scanFailed
          ? " · couldn't be checked for credentials - only the uploader and admins see it"
          : " · being checked for credentials - only the uploader and admins see it for now"
        : "";
  const mayRemove = !!viewer && !!onRemove && canRemoveMedia(viewer, { uploadedById: s.mine ? viewer.id : null });
  return (
    <div className="lightbox" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lb-bar" onClick={(e) => e.stopPropagation()}>
        <span>
          #{item.num} &middot; {noun} {i + 1} of {n}
          {s.noteId && " · added with a note"}
          {thumbOnly && " · thumbnail only - the full-size image wasn't in the original board"}
          {state && <span className="lb-state">{state}</span>}
        </span>
        <span className="sp" />
        {n > 1 && <button onClick={() => step(-1)}>&larr; Prev</button>}
        {n > 1 && <button onClick={() => step(1)}>Next &rarr;</button>}
        {mayRemove && <button onClick={() => onRemove!(s.id)}>Remove</button>}
        <button onClick={onClose}>Close</button>
      </div>
      {!s.thumb && !s.full ? (
        <div className="lb-wait">{s.failed ? `This ${noun} couldn't be used: ${s.failed}` : `Getting this ${noun} ready…`}</div>
      ) : video ? (
        <video
          key={s.id}
          src={s.full ?? undefined}
          poster={s.poster ?? s.thumb}
          controls
          playsInline
          preload="metadata"
          aria-label={`Screen recording ${i + 1} for item ${item.num}`}
        />
      ) : (
        <img src={s.full ?? s.thumb} alt={`Screenshot ${i + 1} for item ${item.num}`} className={thumbOnly ? "lb-archived" : undefined} />
      )}
    </div>
  );
}
